<template>
    <div>
        <div class="flex flex-wrap">
            <div class="sm:bg-base-200/50 flex w-full flex-col items-center justify-center gap-8 p-6 text-center
                        sm:w-1/2">
                <h1>{{ page.heading }}</h1>
                <p class="leading-loose">
                    {{ page.intro }}
                </p>
                <div class="flex flex-wrap justify-center gap-4">
                    <NuxtLink :to="{ name: 'contact' }" class="btn btn-primary hover:!text-primary-content/70">
                        Enquire Now
                    </NuxtLink>
                    <NuxtLink v-if="page.secondaryLink"
                              :to="page.secondaryLink.to"
                              class="btn btn-outline">
                        {{ page.secondaryLink.label }}
                    </NuxtLink>
                </div>
            </div>
            <NuxtPicture format="auto" legacy-format="auto"
                         :src="page.image.src"
                         sizes="sm:100vw md:50vw"
                         loading="eager"
                         fetchpriority="high"
                         preload
                         :width="page.image.width"
                         :height="page.image.height"
                         :alt="page.image.alt"
                         :img-attrs="{
                             class: 'h-full w-full object-center object-cover'
                         }"
                         class="w-full sm:w-1/2 md:max-h-96" />
        </div>

        <div class="container my-12 space-y-16 leading-loose">
            <section v-for="section in page.sections" :key="section.heading" class="mx-auto max-w-3xl space-y-4">
                <h2>{{ section.heading }}</h2>
                <p v-for="(paragraph, i) in section.paragraphs" :key="i">
                    {{ paragraph }}
                </p>
                <table v-if="section.table" class="table">
                    <thead>
                        <tr>
                            <th v-for="header in section.table.headers" :key="header">
                                {{ header }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in section.table.rows" :key="i">
                            <td v-for="(cell, j) in row" :key="j">
                                {{ cell }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <section v-if="page.products?.length" class="mx-auto max-w-3xl space-y-4">
                <h2>{{ page.productsHeading ?? 'Popular choices' }}</h2>
                <dl class="space-y-4">
                    <div v-for="product in page.products" :key="product.slug">
                        <dt class="font-bold">
                            <NuxtLink :to="`/products/${product.slug}`" class="link">
                                {{ product.name }}
                            </NuxtLink>
                        </dt>
                        <dd v-if="product.blurb">
                            {{ product.blurb }}
                        </dd>
                    </div>
                </dl>
            </section>

            <section v-if="page.faqs?.length" class="mx-auto max-w-3xl space-y-4">
                <h2>Questions</h2>
                <div v-for="faq in page.faqs" :key="faq.question">
                    <h3 class="font-bold">
                        {{ faq.question }}
                    </h3>
                    <p>{{ faq.answer }}</p>
                </div>
            </section>

            <div class="text-center">
                <NuxtLink :to="{ name: 'contact' }" class="btn btn-primary hover:!text-primary-content/70">
                    Get in Touch
                </NuxtLink>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { LandingPage } from '~/fixtures';

const props = defineProps<{ page: LandingPage }>();

defineOptions({
    name: 'PublicLandingPage'
});

const title = `${props.page.heading} | Cina's Delicious Cakes`;

useSeoMeta({
    title,
    description: props.page.metaDescription,
    ogTitle: title,
    ogDescription: props.page.metaDescription,
    twitterTitle: title
});

useSchemaOrg(props.page.faqs?.length
    ? [defineWebPage({ '@type': 'FAQPage' }), ...props.page.faqs.map(({ question, answer }) => defineQuestion({
            name: question,
            acceptedAnswer: { text: answer }
        }))]
    : [defineWebPage()]);
</script>
